(() => {
  const ROOT_SELECTOR = '[data-woopf-complete-homepage]';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const FINE_POINTER = '(hover: hover) and (pointer: fine)';

  const routeLinks = [
    { label: 'Shop', href: '/collections/all-products' },
    { label: 'Best sellers', href: '/collections/best-sellers' },
    { label: 'Bundles', href: '/collections/bundles' },
    { label: 'Size helper', href: '/pages/size-configurator' },
    { label: 'Journal', href: '/blogs/news' }
  ];

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function initRoot(root) {
    if (!root || root.dataset.woopfHomepageReady === 'true') return;
    root.dataset.woopfHomepageReady = 'true';

    const cleanups = [];
    const on = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const reducedMotion = window.matchMedia(REDUCED_MOTION).matches;
    const finePointer = window.matchMedia(FINE_POINTER).matches;
    root.classList.add('woopf-experience-ready');
    if (!reducedMotion) root.classList.add('woopf-motion-ready');

    /* Section IDs for progressive-navigation and analytics. */
    const sectionMap = [
      ['.woopf-products', 'woopf-featured'],
      ['.woopf-reviews', 'woopf-reviews'],
      ['.woopf-needs', 'shop-by-need'],
      ['.woopf-life-health', 'woopf-life-health'],
      ['.woopf-bundles', 'woopf-bundles'],
      ['.woopf-founder', 'woopf-founder'],
      ['.woopf-confidence', 'woopf-confidence'],
      ['.woopf-journal', 'woopf-journal'],
      ['.woopf-smart-safety', 'woopf-safety'],
      ['.woopf-faq', 'woopf-faq'],
      ['.woopf-newsletter', 'woopf-newsletter'],
      ['.woopf-family', 'woopf-family']
    ];
    sectionMap.forEach(([selector, id]) => {
      const section = root.querySelector(selector);
      if (section && !section.id) section.id = id;
    });

    /* Scroll progress — tiny, useful and visually quiet. */
    const progress = el('div', 'woopf-page-progress');
    progress.setAttribute('aria-hidden', 'true');
    const progressBar = el('span');
    progress.append(progressBar);
    root.prepend(progress);

    const hero = root.querySelector('[data-woopf-hero-section]');

    /* Floating route dock — appears only after the opening section. */
    const dock = el('div', 'woopf-route-dock');
    dock.setAttribute('aria-label', 'Quick WOOPF navigation');
    const dockToggle = el('button', 'woopf-route-dock__toggle', 'Explore');
    dockToggle.type = 'button';
    dockToggle.setAttribute('aria-expanded', 'false');
    dockToggle.setAttribute('aria-label', 'Open quick navigation');
    const dockPanel = el('nav', 'woopf-route-dock__panel');
    dockPanel.setAttribute('aria-label', 'Quick links');
    routeLinks.forEach((item) => {
      const a = el('a', '', item.label);
      a.href = item.href;
      dockPanel.append(a);
    });
    const about = el('a', 'woopf-route-dock__about', 'Meet WOOPF');
    about.href = '/pages/about-us';
    dockPanel.append(about);
    dock.append(dockToggle, dockPanel);
    root.append(dock);

    on(dockToggle, 'click', () => {
      const open = dock.classList.toggle('is-open');
      dockToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      dockToggle.textContent = open ? 'Close' : 'Explore';
    });
    on(document, 'keydown', (event) => {
      if (event.key === 'Escape' && dock.classList.contains('is-open')) {
        dock.classList.remove('is-open');
        dockToggle.setAttribute('aria-expanded', 'false');
        dockToggle.textContent = 'Explore';
        dockToggle.focus();
      }
    });

    /* Entrance choreography. */
    const revealTargets = root.querySelectorAll([
      '.woopf-heading',
      '.woopf-split-heading',
      '.woopf-reviews__intro',
      '.woopf-life-health__intro',
      '.woopf-confidence__intro',
      '.woopf-founder__copy',
      '.woopf-founder__media',
      '.woopf-newsletter__grid',
      '.woopf-family__grid',
      '.woopf-product-card',
      '.woopf-review',
      '.woopf-need-card',
      '.woopf-life-card',
      '.woopf-bundle-card',
      '.woopf-confidence__card',
      '.woopf-category',
      '.woopf-guide__card',
      '.woopf-journal article',
      '.woopf-smart-safety__product',
      '.woopf-faq details',
      '.woopf-brand'
    ].join(','));

    revealTargets.forEach((node, index) => {
      node.classList.add('woopf-reveal');
      node.style.setProperty('--reveal-order', index % 8);
    });

    let revealObserver = null;
    if (!reducedMotion && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      revealTargets.forEach((node) => revealObserver.observe(node));
      cleanups.push(() => revealObserver.disconnect());
    } else {
      revealTargets.forEach((node) => node.classList.add('is-visible'));
    }

    /* Hero depth follows pointer very subtly. */
    const heroMedia = root.querySelector('.woopf-hero__media');
    if (heroMedia && finePointer && !reducedMotion) {
      const pointerMove = (event) => {
        const rect = heroMedia.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        heroMedia.style.setProperty('--hero-x', x.toFixed(3));
        heroMedia.style.setProperty('--hero-y', y.toFixed(3));
      };
      const pointerLeave = () => {
        heroMedia.style.setProperty('--hero-x', 0);
        heroMedia.style.setProperty('--hero-y', 0);
      };
      on(heroMedia, 'pointermove', pointerMove, { passive: true });
      on(heroMedia, 'pointerleave', pointerLeave, { passive: true });
    }

    /* Card spotlight + restrained 3D tilt on product/collection cards. */
    const interactiveCards = root.querySelectorAll([
      '.woopf-product-card',
      '.woopf-bundle-card',
      '.woopf-category',
      '.woopf-need-card',
      '.woopf-guide__card',
      '.woopf-confidence__card',
      '.woopf-brand'
    ].join(','));

    if (finePointer && !reducedMotion) {
      interactiveCards.forEach((card) => {
        const move = (event) => {
          const rect = card.getBoundingClientRect();
          const px = (event.clientX - rect.left) / rect.width;
          const py = (event.clientY - rect.top) / rect.height;
          card.style.setProperty('--pointer-x', `${(px * 100).toFixed(1)}%`);
          card.style.setProperty('--pointer-y', `${(py * 100).toFixed(1)}%`);
          card.style.setProperty('--tilt-x', `${((0.5 - py) * 2.2).toFixed(2)}deg`);
          card.style.setProperty('--tilt-y', `${((px - 0.5) * 2.8).toFixed(2)}deg`);
        };
        const leave = () => {
          card.style.setProperty('--tilt-x', '0deg');
          card.style.setProperty('--tilt-y', '0deg');
        };
        on(card, 'pointermove', move, { passive: true });
        on(card, 'pointerleave', leave, { passive: true });
      });
    }

    /* Magnetic primary actions. */
    if (finePointer && !reducedMotion) {
      root.querySelectorAll('.woopf-button, .woopf-card-cta').forEach((button) => {
        const move = (event) => {
          const rect = button.getBoundingClientRect();
          const x = event.clientX - rect.left - rect.width / 2;
          const y = event.clientY - rect.top - rect.height / 2;
          button.style.setProperty('--mag-x', `${(x * 0.08).toFixed(1)}px`);
          button.style.setProperty('--mag-y', `${(y * 0.10).toFixed(1)}px`);
        };
        const leave = () => {
          button.style.setProperty('--mag-x', '0px');
          button.style.setProperty('--mag-y', '0px');
        };
        on(button, 'pointermove', move, { passive: true });
        on(button, 'pointerleave', leave, { passive: true });
      });
    }

    /* Horizontal scrollers get a live progress rail on small screens. */
    const scrollers = root.querySelectorAll('.woopf-product-grid, .woopf-review-grid, .woopf-bundle-grid');
    scrollers.forEach((scroller) => {
      const rail = el('div', 'woopf-swipe-progress');
      rail.setAttribute('aria-hidden', 'true');
      const thumb = el('span');
      rail.append(thumb);
      scroller.insertAdjacentElement('afterend', rail);
      const updateRail = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        const ratio = max > 0 ? scroller.scrollLeft / max : 0;
        thumb.style.transform = `scaleX(${Math.max(0.18, 0.18 + ratio * 0.82)})`;
        rail.classList.toggle('is-active', max > 8);
      };
      on(scroller, 'scroll', updateRail, { passive: true });
      on(window, 'resize', updateRail, { passive: true });
      updateRail();
    });

    /* Global scroll-driven polish: progress, dock visibility, parallax variables. */
    const mobileCta = root.querySelector('[data-woopf-mobile-cta]');
    const stop = root.querySelector('.woopf-newsletter');
    let raf = null;
    const updateScroll = () => {
      raf = null;
      const doc = document.documentElement;
      const maxScroll = Math.max(1, doc.scrollHeight - window.innerHeight);
      progressBar.style.transform = `scaleX(${Math.min(1, Math.max(0, window.scrollY / maxScroll))})`;

      const heroBottom = hero ? hero.getBoundingClientRect().bottom : 0;
      const nearStop = stop ? stop.getBoundingClientRect().top < window.innerHeight - 84 : false;
      dock.classList.toggle('is-visible', heroBottom < 80 && !nearStop);

      if (mobileCta && hero) {
        const visible = heroBottom < 24 && !nearStop;
        mobileCta.classList.toggle('is-visible', visible);
        mobileCta.setAttribute('aria-hidden', visible ? 'false' : 'true');
        mobileCta.tabIndex = visible ? 0 : -1;
      }

      if (!reducedMotion) {
        root.style.setProperty('--woopf-scroll-y', `${window.scrollY}px`);
      }
    };
    const queueScroll = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(updateScroll);
    };
    on(window, 'scroll', queueScroll, { passive: true });
    on(window, 'resize', queueScroll, { passive: true });
    updateScroll();

    /* CTA interaction event hook for analytics containers. */
    const trackClick = (event) => {
      const link = event.target.closest('a, button');
      if (!link || !root.contains(link) || !Array.isArray(window.dataLayer)) return;
      const label = (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120);
      if (!label) return;
      window.dataLayer.push({
        event: 'woopf_home_interaction',
        interaction_label: label,
        interaction_href: link.href || '',
        interaction_section: link.closest('.woopf-section, .woopf-compass')?.id || ''
      });
    };
    on(root, 'click', trackClick);

    root.woopfHomepageCleanup = () => {
      cleanups.forEach((cleanup) => cleanup());
      if (raf !== null) window.cancelAnimationFrame(raf);
      progress.remove();
      dock.remove();
      root.querySelectorAll('.woopf-swipe-progress').forEach((node) => node.remove());
      revealTargets.forEach((node) => {
        node.classList.remove('woopf-reveal', 'is-visible');
        node.style.removeProperty('--reveal-order');
      });
      interactiveCards.forEach((card) => {
        card.style.removeProperty('--pointer-x');
        card.style.removeProperty('--pointer-y');
        card.style.removeProperty('--tilt-x');
        card.style.removeProperty('--tilt-y');
      });
      root.classList.remove('woopf-experience-ready', 'woopf-motion-ready');
      root.style.removeProperty('--woopf-scroll-y');
      delete root.dataset.woopfHomepageReady;
      delete root.woopfHomepageCleanup;
    };
  }

  function initWithin(container) {
    if (!container) return;
    if (container.matches?.(ROOT_SELECTOR)) initRoot(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    if (root && typeof root.woopfHomepageCleanup === 'function') root.woopfHomepageCleanup();
  });
})();

/* ==========================================================================
   WOOPF shoppable video wall
   Lazy video, one-audio-at-a-time, category filters and a lightbox.
   Progressive enhancement: without JS the rail still scrolls, the posters
   still show and every call to action is a plain link to a product page.
   ========================================================================== */
(() => {
  const SECTION = '[data-woopf-reels]';
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const saveData = () => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    return ['slow-2g', '2g'].indexOf(c.effectiveType) !== -1;
  };

  const track = (event, payload) => {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  };

  function initSection(root) {
    if (!root || root.dataset.woopfReelsReady === 'true') return;
    root.dataset.woopfReelsReady = 'true';

    const rail = root.querySelector('[data-woopf-reel-rail]');
    if (!rail) return;

    const reels = Array.from(root.querySelectorAll('[data-woopf-reel]'));
    const allowAutoplay = root.dataset.autoplay !== 'false' && !REDUCED.matches && !saveData();
    let audioReel = null;

    /* ---- Lazy source attachment ----------------------------------------- */
    const attach = (reel) => {
      const video = reel.querySelector('[data-woopf-reel-video]');
      if (!video || video.dataset.loaded === 'true') return video;
      const src = video.dataset.src;
      if (!src) return video;
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      video.appendChild(source);
      video.dataset.loaded = 'true';
      video.load();
      return video;
    };

    const play = (reel) => {
      const video = attach(reel);
      if (!video) return;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    };

    const pause = (reel) => {
      const video = reel.querySelector('[data-woopf-reel-video]');
      if (video && !video.paused) video.pause();
    };

    if ('IntersectionObserver' in window) {
      const loader = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) attach(entry.target);
        });
      }, { root: rail, rootMargin: '280px 400px', threshold: 0 });

      const player = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const reel = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            if (allowAutoplay || reel === audioReel) play(reel);
          } else if (reel !== audioReel) {
            pause(reel);
          }
        });
      }, { threshold: [0, 0.55, 0.9] });

      reels.forEach((reel) => {
        loader.observe(reel);
        player.observe(reel);
      });

      /* Stop every clip while the section is off-screen — saves battery and data. */
      const sectionWatcher = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) return;
          reels.forEach(pause);
          if (audioReel) {
            muteReel(audioReel);
            audioReel = null;
          }
        });
      }, { threshold: 0 });
      sectionWatcher.observe(root);
    } else {
      reels.forEach(attach);
    }

    /* ---- Sound: exactly one clip may speak at a time ---------------------- */
    function muteReel(reel) {
      const video = reel.querySelector('[data-woopf-reel-video]');
      const button = reel.querySelector('[data-woopf-reel-sound]');
      if (video) video.muted = true;
      if (button) button.setAttribute('aria-pressed', 'false');
    }

    function unmuteReel(reel) {
      if (audioReel && audioReel !== reel) muteReel(audioReel);
      const video = attach(reel);
      const button = reel.querySelector('[data-woopf-reel-sound]');
      if (!video) return;
      video.muted = false;
      video.volume = 1;
      play(reel);
      if (button) button.setAttribute('aria-pressed', 'true');
      audioReel = reel;
      track('woopf_reel_sound_on', { reel_title: reel.dataset.reelTitle || '' });
    }

    root.addEventListener('click', (event) => {
      const soundButton = event.target.closest('[data-woopf-reel-sound]');
      if (soundButton) {
        event.preventDefault();
        const reel = soundButton.closest('[data-woopf-reel]');
        if (!reel) return;
        if (audioReel === reel) {
          muteReel(reel);
          audioReel = null;
        } else {
          unmuteReel(reel);
        }
        return;
      }

      const opener = event.target.closest('[data-woopf-reel-open]');
      if (opener) {
        event.preventDefault();
        const reel = opener.closest('[data-woopf-reel]');
        if (reel) openViewer(reel);
      }
    });

    /* Tapping the video itself is the gesture people already know. */
    reels.forEach((reel) => {
      const video = reel.querySelector('[data-woopf-reel-video]');
      if (!video) return;
      video.addEventListener('click', () => {
        if (audioReel === reel) {
          muteReel(reel);
          audioReel = null;
        } else {
          unmuteReel(reel);
        }
      });
    });

    /* ---- Category filters ------------------------------------------------- */
    const chips = Array.from(root.querySelectorAll('[data-woopf-reel-filter]'));
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const value = chip.dataset.woopfReelFilter;
        chips.forEach((other) => {
          const active = other === chip;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        reels.forEach((reel) => {
          const show = value === 'all' || reel.dataset.reelCategory === value;
          reel.classList.toggle('is-filtered-out', !show);
          if (!show) pause(reel);
        });
        if (audioReel && audioReel.classList.contains('is-filtered-out')) {
          muteReel(audioReel);
          audioReel = null;
        }
        rail.scrollTo({ left: 0, behavior: REDUCED.matches ? 'auto' : 'smooth' });
        updateRail();
        track('woopf_reel_filter', { reel_filter: value });
      });
    });

    /* ---- Arrows and progress --------------------------------------------- */
    const prev = root.querySelector('[data-woopf-reel-prev]');
    const next = root.querySelector('[data-woopf-reel-next]');
    const progress = root.querySelector('[data-woopf-reel-progress]');
    const progressThumb = progress ? progress.querySelector('span') : null;

    const step = () => {
      const card = reels.find((reel) => !reel.classList.contains('is-filtered-out'));
      const width = card ? card.getBoundingClientRect().width + 16 : 260;
      return Math.max(width, Math.floor(rail.clientWidth * 0.8 / width) * width);
    };

    function updateRail() {
      const max = rail.scrollWidth - rail.clientWidth;
      const ratio = max > 0 ? rail.scrollLeft / max : 0;
      if (progressThumb) {
        const visible = max > 0 ? rail.clientWidth / rail.scrollWidth : 1;
        progressThumb.style.transform = 'scaleX(' + Math.max(0.12, visible) + ')';
        progressThumb.style.marginLeft = (ratio * (1 - Math.max(0.12, visible)) * 100) + '%';
      }
      if (progress) progress.style.display = max > 8 ? '' : 'none';
      if (prev) prev.disabled = rail.scrollLeft <= 4;
      if (next) next.disabled = rail.scrollLeft >= max - 4;
    }

    if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: REDUCED.matches ? 'auto' : 'smooth' }));
    if (next) next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: REDUCED.matches ? 'auto' : 'smooth' }));
    rail.addEventListener('scroll', updateRail, { passive: true });
    window.addEventListener('resize', updateRail, { passive: true });
    updateRail();

    /* ---- Lightbox --------------------------------------------------------- */
    const viewer = root.querySelector('[data-woopf-reel-viewer]');
    const stage = viewer ? viewer.querySelector('[data-woopf-reel-viewer-stage]') : null;
    const buySlot = viewer ? viewer.querySelector('[data-woopf-reel-viewer-buy]') : null;
    let viewerIndex = -1;
    let lastFocus = null;

    const visibleReels = () => reels.filter((reel) => !reel.classList.contains('is-filtered-out'));

    function renderViewer(reel) {
      if (!viewer || !stage || !buySlot) return;
      const source = reel.querySelector('[data-woopf-reel-video]');
      stage.innerHTML = '';
      buySlot.innerHTML = '';

      const video = document.createElement('video');
      video.src = source ? source.dataset.src : '';
      if (source && source.getAttribute('poster')) video.setAttribute('poster', source.getAttribute('poster'));
      video.setAttribute('playsinline', '');
      video.setAttribute('controls', '');
      video.loop = true;
      video.autoplay = true;
      video.muted = false;
      video.setAttribute('aria-label', reel.dataset.reelTitle || 'WOOPF video');
      stage.appendChild(video);

      const buy = reel.querySelector('.woopf-reel__buy');
      if (buy) buySlot.appendChild(buy.cloneNode(true));

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      }
    }

    function openViewer(reel) {
      if (!viewer) return;
      const list = visibleReels();
      viewerIndex = list.indexOf(reel);
      if (viewerIndex < 0) return;
      lastFocus = document.activeElement;
      reels.forEach(pause);
      if (audioReel) { muteReel(audioReel); audioReel = null; }
      viewer.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      renderViewer(reel);
      const close = viewer.querySelector('[data-woopf-reel-close]');
      if (close && close.focus) close.focus();
      track('woopf_reel_open', { reel_title: reel.dataset.reelTitle || '' });
    }

    function closeViewer() {
      if (!viewer || viewer.hidden) return;
      viewer.hidden = true;
      if (stage) stage.innerHTML = '';
      if (buySlot) buySlot.innerHTML = '';
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      viewerIndex = -1;
    }

    function shiftViewer(delta) {
      const list = visibleReels();
      if (!list.length) return;
      viewerIndex = (viewerIndex + delta + list.length) % list.length;
      renderViewer(list[viewerIndex]);
    }

    if (viewer) {
      viewer.addEventListener('click', (event) => {
        if (event.target.closest('[data-woopf-reel-close]')) {
          event.preventDefault();
          closeViewer();
          return;
        }
        if (event.target.closest('[data-woopf-reel-viewer-prev]')) { shiftViewer(-1); return; }
        if (event.target.closest('[data-woopf-reel-viewer-next]')) { shiftViewer(1); }
      });

      document.addEventListener('keydown', (event) => {
        if (viewer.hidden) return;
        if (event.key === 'Escape') closeViewer();
        if (event.key === 'ArrowLeft') shiftViewer(-1);
        if (event.key === 'ArrowRight') shiftViewer(1);
      });
    }

  }

  function initWithin(container) {
    if (!container) return;
    if (container.matches && container.matches(SECTION)) initSection(container);
    if (container.querySelectorAll) container.querySelectorAll(SECTION).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();

/* ==========================================================================
   Hero thumbnail gallery
   The thumbnails swap the full-bleed photo and the product card together.
   Everything is server-rendered with item 0 active, so this only takes over
   an already-correct hero. Self-contained so it survives section re-renders.
   ========================================================================== */
(() => {
  const SELECTOR = '[data-woopf-hero-gallery]';

  function initGallery(root) {
    if (!root || root.dataset.woopfGalleryReady === 'true') return;
    root.dataset.woopfGalleryReady = 'true';

    const strip = root.querySelector('[data-woopf-hero-thumbs]');
    if (!strip) return;
    const thumbs = Array.from(strip.querySelectorAll('.woopf-hero__thumb'));
    const frames = Array.from(root.querySelectorAll('.woopf-hero__frame'));
    const cards = Array.from(root.querySelectorAll('.woopf-hero__product'));
    // The primary button lives in the copy column, outside this root.
    const hero = root.closest('.woopf-hero') || document;
    const cta = hero.querySelector('[data-woopf-hero-cta]');
    const ctaHome = cta ? { href: cta.getAttribute('href'), text: cta.textContent } : null;
    if (thumbs.length < 2 || frames.length < 2) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(3000, parseInt(strip.dataset.interval, 10) || 5000);
    let current = 0;
    let timer = null;
    let paused = false;

    const pick = (list, i) => list.find((el) => el.dataset.frame === String(i));

    function show(next) {
      current = (next + thumbs.length) % thumbs.length;
      frames.forEach((f, i) => f.classList.toggle('is-active', i === current));
      // Cards can be fewer than frames if a product resolved blank.
      if (cards.length) {
        const wanted = pick(cards, current) || cards[0];
        cards.forEach((c) => c.classList.toggle('is-active', c === wanted));
      }
      thumbs.forEach((t, i) => {
        const on = i === current;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // Send the main button wherever the visible product goes. The label is
      // left alone deliberately - only the destination follows the carousel.
      if (cta && ctaHome) {
        const picked = thumbs[current];
        cta.setAttribute('href', picked.dataset.url || ctaHome.href);
        cta.textContent = picked.dataset.cta || ctaHome.text;
      }
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      if (reduced || paused || timer) return;
      timer = setInterval(() => show(current + 1), interval);
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => { show(i); stop(); start(); });
      // Previewing on hover and focus means auto-advance never pulls the image
      // out from under someone who is about to choose.
      thumb.addEventListener('mouseenter', () => show(i));
      thumb.addEventListener('focus', () => show(i));
    });

    ['mouseenter', 'focusin'].forEach((type) =>
      root.addEventListener(type, () => { paused = true; stop(); }));
    ['mouseleave', 'focusout'].forEach((type) =>
      root.addEventListener(type, () => { paused = false; start(); }));

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop(); else start();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) start(); else stop(); });
      }, { threshold: 0.2 }).observe(root);
    } else {
      start();
    }

    show(0);
  }

  function initWithin(container) {
    if (!container) return;
    if (container.matches && container.matches(SELECTOR)) initGallery(container);
    if (container.querySelectorAll) container.querySelectorAll(SELECTOR).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();
